import { startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { Booking, Resource, WorkingHoursProfile, DurationType, HalfDayPeriod, WhatIfBookingReduction, WhatIfReductionDay } from '@/types';
import { getBookingSegments, bookingSegmentsOverlap } from './booking-conflict';
import { normalizeDurationType, getWorkingHoursForDay } from './heatmap-utils';
import { selectOverlapSurplus, partitionConflictBookings } from './resolve-conflict-selection';
import type { RebalancingSuggestion } from '@/lib/utils/burnout-risk';

/**
 * Capacity-aware distribution for the surplus bookings produced when resolving
 * an over-utilisation conflict.
 *
 * The surplus set (the bookings that must move, keeping one anchor per overlap
 * cluster) is already computed upstream by `selectOverlapSurplus` /
 * `partitionConflictBookings`. This module answers the *destination* question:
 *
 *   1. First try to place ALL surplus bookings on a SINGLE eligible resource
 *      that has enough free capacity for the affected days (≤100% per day and
 *      no time clash). Prefer the best-scoring such resource — same-role bonus
 *      + skill overlap + free capacity, mirroring the intent of
 *      `generateSuggestions`.
 *   2. Only when no single resource can absorb the whole surplus, spread the
 *      bookings greedily across multiple resources (best fit per booking),
 *      never pushing any resource over 100% on any affected day. A multi-day
 *      booking that no single resource can take whole is split per day, each
 *      slice staying on its ORIGINAL day.
 *   3. Anything that fits nowhere on a safe resource on its OWN dates stays
 *      blocked and surfaces as "No suitable resource found".
 *
 * Booking DATES/TIMES never change — only the assigned resource does. Free
 * capacity and candidate ranking are assessed over the booking's OWN dates
 * (not the surrounding week): a candidate is "under-utilised on the required
 * dates" only when it has headroom on the exact days the surplus occupies.
 *
 * Every placement is additionally VERIFIED against the weekly burnout model:
 * after absorbing a booking, the target's utilisation for each affected ISO
 * week must stay ≤100% (`projectedWeeklyUtilisation`). This guarantees a move
 * never lands work on a resource that would itself become over-utilised — a
 * resource already loaded elsewhere in the same week is rejected even when it
 * looks free on the surplus day.
 *
 * The result is a per-booking `bookingId → candidate` mapping so callers can
 * build `WhatIfScenario.bookingAssignments`, drive the ghost-tile preview and
 * commit the reassignment — all from the same plan, so the calendar preview
 * and the "View suggestions" overlay always agree.
 */

const EPSILON = 1e-6;

/**
 * The weekly utilisation a rebalance must bring a resource BELOW. `computeBurnoutRisk`
 * classes a week at 100–110% as high risk, so a week sitting at exactly 100% is
 * still flagged — the minimal surplus selection therefore has to drop it under
 * this limit, not merely to it.
 */
const WEEK_UTILISATION_LIMIT_PCT = 100;

const DEFAULT_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0,
};

export interface BookingDaySlice {
  /** YYYY-MM-DD of the single day this slice covers. */
  dayKey: string;
  /** ISO start of the slice (bounds the day for full/half/hourly). */
  startTime: string;
  /** ISO end of the slice. */
  endTime: string;
  /** Duration shape of the slice (full-day / half-day-* / hourly). */
  durationType: DurationType;
  /** Half-day period carried through for half-day slices. */
  halfDayPeriod?: HalfDayPeriod;
}

export interface BookingAssignment {
  bookingId: string;
  candidateResourceId: string;
  candidateResourceName: string;
  /**
   * The concrete day + duration shape this assignment lands on, present ONLY
   * when a multi-day source booking was SPLIT across resources because no single
   * one was free across all its days — each day is placed on a resource with
   * room THAT day, but every slice stays on its ORIGINAL day (the date never
   * changes, only the resource). The ghost preview and commit use these resolved
   * per-day dates. When absent, the whole booking moves to `candidateResourceId`
   * on its original day(s) as a single unit.
   */
  daySlice?: BookingDaySlice;
}

export type DistributionMode = 'single' | 'spread' | 'none';

/** Why one specific candidate was (or was not) able to take a blocked booking. */
export interface CandidateRejectionDiagnostic {
  candidateId: string;
  candidateName: string;
  /** True when the candidate was removed up-front via `excludeResourceIds`. */
  excluded: boolean;
  /**
   * The placement evaluation against the candidate's BASELINE load. `ok: false`
   * carries the concrete rejection reason + capacity numbers. `ok: true` means
   * the candidate could take it on its own dates in isolation — so the booking
   * was blocked only by run-time contention (it absorbed other surplus first)
   * or by being excluded, which is itself a useful diagnosis.
   */
  result: PlacementResult;
}

/** Per-blocked-booking record of why every candidate failed to take it. */
export interface BlockedBookingDiagnostic {
  bookingId: string;
  /** The distinct calendar-day keys the booking occupies (dates never change). */
  bookingDateKeys: string[];
  /** Normalised duration shape (full-day / half-day* / hourly). */
  durationType: DurationType;
  /** One entry per real candidate considered (eligible AND excluded). */
  rejections: CandidateRejectionDiagnostic[];
}

export interface SurplusDistributionResult {
  /** 'single' when one resource absorbs everything, 'spread' when split, 'none' when nothing could be placed. */
  mode: DistributionMode;
  /** One entry per surplus booking that found a home. */
  assignments: BookingAssignment[];
  /** Distinct destination resource ids, in placement/priority order. */
  targetResourceIds: string[];
  /** Surplus booking ids that fit nowhere without breaching capacity. */
  blockedBookingIds: string[];
  /**
   * Resource ids that were removed from consideration up-front via
   * `excludeResourceIds` (typically because they are themselves at burnout
   * risk). Surfaced so callers can EXPLAIN why the plan may report
   * "no suitable resource" — the capacity may exist but sit on a resource that
   * was intentionally protected from more work.
   */
  excludedResourceIds: string[];
  /**
   * Per-blocked-booking breakdown of WHY every candidate was rejected. Only
   * populated when diagnostics are enabled (the `debug` param or the module
   * debug flag). Lets callers replace the generic "every safe resource is
   * already full" copy with the concrete reason (`day-capacity`,
   * `week-capacity`, `time-clash`).
   */
  blockedDiagnostics?: BlockedBookingDiagnostic[];
}

export interface DistributeSurplusParams {
  /** The surplus bookings that must be reassigned. */
  surplus: Booking[];
  /** The full candidate pool (typically every resource). */
  resources: Resource[];
  /** All current bookings — used to measure each candidate's existing load. */
  existingBookings: Booking[];
  /** Global working-hours profile (fallback when a resource has no custom one). */
  globalProfile: WorkingHoursProfile;
  /** The over-utilised resource whose surplus is being moved (excluded as a target). */
  atRiskResourceId?: string;
  /**
   * Resource whose skills/role define the "same role" bonus and skill overlap
   * when scoring candidates. Defaults to the at-risk resource.
   */
  atRiskResource?: Resource | null;
  /**
   * A resource the caller would like to use as the single absorber when it can
   * take everything (e.g. the candidate the user picked in the overlay). It is
   * only honoured when it is eligible and has capacity for the whole surplus.
   */
  preferredResourceId?: string;
  /**
   * Resources that must never receive a moving booking — typically every other
   * resource that is ALREADY at burnout risk. Redistributing onto an at-risk
   * resource would just re-trigger the alert on them, so they are excluded from
   * the eligible pool even when they have nominal day-level capacity.
   */
  excludeResourceIds?: string[];
  /**
   * When true, collect per-candidate rejection reasons for every blocked
   * booking and `console.debug` them, and attach them as `blockedDiagnostics`
   * on the result. Defaults to the module debug flag
   * (`globalThis.__EB_SURPLUS_DISTRIBUTION_DEBUG__`) so the overlay can be
   * traced in the browser console without a code change.
   */
  debug?: boolean;
}

// ── Working-hours capacity helpers ───────────────────────────────────────────

/** Local Date at noon for a YYYY-MM-DD key (noon avoids DST/timezone edge cases). */
function dateFromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** The distinct calendar-day keys a booking occupies. */
function bookingDateKeys(booking: Booking): string[] {
  return Array.from(new Set(getBookingSegments(booking).map((s) => s.dateKey)));
}

/** Local YYYY-MM-DD key for a Date. */
function dateKeyOf(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The Mon–Sun (`weekStartsOn: 1`) calendar-day keys of the ISO week containing
 * `dayKey`. Used ONLY to VERIFY a candidate's weekly utilisation after a
 * placement — never to move a booking to a different day. Mirrors the weekly
 * window `computeWeekUtilisation` (burnout-risk.ts) uses.
 */
function isoWeekDaysFor(dayKey: string): string[] {
  const d = dateFromKey(dayKey);
  const start = startOfWeek(d, { weekStartsOn: 1 });
  const end = endOfWeek(d, { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end }).map(dateKeyOf);
}

/**
 * The REAL hours a booking consumes on a single calendar day — the booking's
 * true size, NEVER capped at the candidate's own day capacity. This mirrors the
 * working-hours accounting used by the display/burnout model
 * (`computeWeekUtilisation`, `calculateTotalBookedHours`) so the capacity gate
 * and the heatmap agree: a booking that is genuinely too big for a day/week is
 * rejected instead of being silently clamped to fit.
 *
 *   • full-day  → the working-profile hours for that day (`dayCapacityHours`)
 *   • half-day  → half the profile hours for that day
 *   • hourly    → the actual wall-clock minutes booked on this day (UNCAPPED)
 *
 * `dayCapacityHours` is the working-profile capacity of the day for the resource
 * whose pattern DEFINES the booking's size. For a booking already hosted on a
 * resource (the candidate's own existing load) that is the candidate's own
 * capacity. For a surplus booking being MOVED, however, its real worked hours
 * come from its ORIGINATING resource's working-hours pattern — a full-day
 * booking made by a 6.5h/day person is 6.5h of work, not the candidate's whole
 * day — so callers pass the ORIGIN's day capacity for the incoming booking (see
 * `distributeSurplusBookings`). This stops a full-day move from automatically
 * consuming the target's entire day and falsely tripping the day/week gate.
 * Hourly hours are the booking's own wall-clock time and are deliberately not
 * reduced to any resource's capacity: if they exceed the remaining room on a
 * day or across the week, `canPlace` must reject the move.
 */
function bookingHoursOnDay(
  booking: Booking,
  dateKey: string,
  dayCapacityHours: number,
): number {
  const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
  if (dt === 'full-day') return dayCapacityHours;
  if (dt === 'half-day' || dt === 'half-day-am' || dt === 'half-day-pm') {
    return dayCapacityHours / 2;
  }
  // hourly — the booking's REAL wall-clock hours on this day, NOT capped at the
  // candidate's day capacity, so an oversized booking cannot be clamped through
  // the capacity gate.
  const segMinutes = getBookingSegments(booking)
    .filter((s) => s.dateKey === dateKey)
    .reduce((sum, s) => sum + (s.endMin - s.startMin), 0);
  return segMinutes / 60;
}

/** True when a booking is a whole-day (`full-day`) allocation. */
function isFullDayBooking(booking: Booking): boolean {
  return normalizeDurationType(booking.durationType, booking.halfDayPeriod) === 'full-day';
}

/**
 * Resolves the real worked hours an INCOMING (moving) booking consumes on a
 * day, measured against its ORIGINATING working-hours pattern rather than the
 * target candidate's. `fallbackCapacity` is used when the origin isn't known.
 */
type IncomingHoursResolver = (
  booking: Booking,
  dateKey: string,
  fallbackCapacity: number,
) => number;

/** ISO datetime string for a minute-of-day on a YYYY-MM-DD key. */
function isoForDayMinute(dayKey: string, minute: number): string {
  if (minute >= 24 * 60) return `${dayKey}T23:59:59.999`;
  const hh = String(Math.floor(minute / 60)).padStart(2, '0');
  const mm = String(minute % 60).padStart(2, '0');
  return `${dayKey}T${hh}:${mm}:00.000`;
}

/**
 * Decompose a (typically multi-day) booking into one virtual booking per day it
 * occupies, preserving the duration shape of that day. Used when no single
 * resource is free across the whole span so the individual days can be spread
 * across resources that DO have room on each day.
 */
function splitBookingByDay(booking: Booking): Array<{ slice: Booking; daySlice: BookingDaySlice }> {
  const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
  return getBookingSegments(booking).map((seg) => {
    let sliceDurationType: DurationType;
    let startTime: string;
    let endTime: string;
    let halfDayPeriod: HalfDayPeriod | undefined;

    if (dt === 'full-day') {
      sliceDurationType = 'full-day';
      startTime = isoForDayMinute(seg.dateKey, 0);
      endTime = `${seg.dateKey}T23:59:59.999`;
    } else if (dt === 'half-day-am' || dt === 'half-day-pm' || dt === 'half-day') {
      sliceDurationType = dt;
      halfDayPeriod = dt === 'half-day-pm' ? 'pm' : dt === 'half-day-am' ? 'am' : booking.halfDayPeriod;
      startTime = isoForDayMinute(seg.dateKey, seg.startMin);
      endTime = isoForDayMinute(seg.dateKey, seg.endMin);
    } else {
      sliceDurationType = 'hourly';
      startTime = isoForDayMinute(seg.dateKey, seg.startMin);
      endTime = isoForDayMinute(seg.dateKey, seg.endMin);
    }

    const daySlice: BookingDaySlice = {
      dayKey: seg.dateKey,
      startTime,
      endTime,
      durationType: sliceDurationType,
      ...(halfDayPeriod ? { halfDayPeriod } : {}),
    };
    const slice: Booking = {
      ...booking,
      id: `${booking.id}__${seg.dateKey}`,
      startTime,
      endTime,
      durationType: sliceDurationType,
      halfDayPeriod,
    };
    return { slice, daySlice };
  });
}

/** The resource's working-hours capacity for a given calendar-day key. */
function dayCapacityFor(
  resource: Resource,
  dateKey: string,
  globalProfile: WorkingHoursProfile,
): number {
  return getWorkingHoursForDay(resource, dateFromKey(dateKey), globalProfile);
}

// ── Scoring ──────────────────────────────────────────────────────────────────

/**
 * Composite candidate score, reusing the intent of `generateSuggestions`:
 * same-role bonus + proficiency-weighted skill overlap + free-capacity signal.
 * Higher is better. `freeCapacityPct` is 0–100 across the affected days.
 */
function scoreCandidate(
  candidate: Resource,
  atRiskResource: Resource | null | undefined,
  freeCapacityPct: number,
): number {
  let skillSignal = 50; // neutral when the at-risk resource has no recorded skills
  const atRiskSkills = atRiskResource?.skills ?? [];
  if (atRiskSkills.length > 0) {
    const skillMap = new Map(atRiskSkills.map((s) => [s.name.toLowerCase(), s]));
    let weighted = 0;
    (candidate.skills ?? []).forEach((cs) => {
      if (skillMap.has(cs.name.toLowerCase())) {
        weighted += (cs.rating / 5) * (1 / skillMap.size) * 100;
      }
    });
    skillSignal = weighted;
  }

  const capacitySignal = Math.max(0, Math.min(100, freeCapacityPct));
  const roleBonus =
    atRiskResource && candidate.role && candidate.role === atRiskResource.role
      ? 10
      : 0;
  return Math.min(100, skillSignal * 0.7 + capacitySignal * 0.3 + roleBonus);
}

// ── Placement simulation ───────────────────────────────────────────────────

interface CandidateState {
  resource: Resource;
  /** dateKey → hours already consumed (existing bookings + placed surplus). */
  usedHours: Map<string, number>;
  /** Bookings currently occupying this candidate (existing + placed surplus). */
  occupied: Booking[];
  /** dateKey → capacity hours (cached). */
  capacity: Map<string, number>;
  /** Global working-hours profile — needed for the weekly over-utilisation check. */
  globalProfile: WorkingHoursProfile;
  /**
   * Real worked hours a MOVING booking adds on a day, measured against its
   * ORIGIN's working-hours pattern (not this candidate's). Occupied bookings
   * keep using this candidate's own capacity via {@link bookingHoursOnDay}.
   */
  incomingHoursOnDay: IncomingHoursResolver;
}

function buildCandidateState(
  resource: Resource,
  affectedDateKeys: string[],
  existingBookings: Booking[],
  globalProfile: WorkingHoursProfile,
  incomingHoursOnDay: IncomingHoursResolver,
): CandidateState {
  const capacity = new Map<string, number>();
  affectedDateKeys.forEach((key) => {
    capacity.set(key, dayCapacityFor(resource, key, globalProfile));
  });

  const occupied = existingBookings.filter(
    (b) =>
      b.status !== 'cancelled' &&
      (String(b.resourceId) === String(resource.id) ||
        (b.resourceIds ?? []).map(String).includes(String(resource.id))),
  );

  const usedHours = new Map<string, number>();
  occupied.forEach((b) => {
    bookingDateKeys(b).forEach((key) => {
      if (!capacity.has(key)) return;
      const cap = capacity.get(key) ?? 0;
      usedHours.set(key, (usedHours.get(key) ?? 0) + bookingHoursOnDay(b, key, cap));
    });
  });

  return { resource, usedHours, occupied, capacity, globalProfile, incomingHoursOnDay };
}

/**
 * Projected utilisation (%) of the candidate for the ISO week(s) that `booking`
 * would land in, AFTER adding it on top of everything already on the candidate
 * (existing bookings + surplus already placed this run). Returns the highest
 * across the affected weeks. Mirrors the weekly working-hours model in
 * `computeWeekUtilisation` (burnout-risk.ts): booked hours ÷ available hours
 * over the Mon–Sun week. Used to confirm a move never pushes the target's
 * weekly load over 100%.
 */
function projectedWeeklyUtilisation(state: CandidateState, booking: Booking): number {
  // The distinct ISO weeks the booking touches (keyed by their Monday).
  const weeksByStart = new Map<string, string[]>();
  bookingDateKeys(booking).forEach((dk) => {
    const days = isoWeekDaysFor(dk);
    weeksByStart.set(days[0], days);
  });

  let peak = 0;
  weeksByStart.forEach((weekDays) => {
    let available = 0;
    let booked = 0;
    weekDays.forEach((day) => {
      const cap = dayCapacityFor(state.resource, day, state.globalProfile);
      available += cap;
      let usedOnDay = 0;
      state.occupied.forEach((occ) => {
        if (bookingDateKeys(occ).includes(day)) {
          usedOnDay += bookingHoursOnDay(occ, day, cap);
        }
      });
      if (bookingDateKeys(booking).includes(day)) {
        usedOnDay += state.incomingHoursOnDay(booking, day, cap);
      }
      booked += usedOnDay;
    });
    const util = available > 0 ? (booked / available) * 100 : booked > 0 ? Infinity : 0;
    if (util > peak) peak = util;
  });
  return peak;
}

/**
 * Why a candidate cannot take a surplus booking.
 *   • `time-clash`    — a genuine wall-clock overlap between two NON-full-day
 *                       bookings. A full-day booking is a whole-day placeholder,
 *                       not a literal lockout, so it never hard-clashes; its
 *                       load is enforced by `day-capacity` instead.
 *   • `day-capacity`  — a single affected DAY would breach 100% (the booking's
 *                       REAL worked hours on that day plus what is already used
 *                       exceed the candidate's working-hours capacity for that
 *                       day). A full-day booking is charged its ORIGIN's day
 *                       hours, so a candidate with only light existing work can
 *                       still absorb a sub-capacity full-day booking.
 *   • `week-capacity` — every day fits, but the candidate's projected WEEKLY
 *                       utilisation would exceed 100% for an affected ISO week.
 */
export type PlacementRejectionReason = 'time-clash' | 'day-capacity' | 'week-capacity';

/**
 * The numbers behind a rejection, captured for diagnostics so a
 * "No suitable resource found" outcome can be explained precisely rather than
 * losing the reason to a boolean.
 */
export interface PlacementRejectionDetail {
  /** The affected day the check failed on (present for day/time-clash checks). */
  dayKey?: string;
  /** The candidate's `dayCapacityFor` value for `dayKey`. */
  dayCapacity?: number;
  /** Hours already used on `dayKey` before this booking (existing + placed). */
  used?: number;
  /** The `bookingHoursOnDay` value this booking adds on `dayKey`. */
  add?: number;
  /** The `projectedWeeklyUtilisation` result (%) — present for `week-capacity`. */
  projectedWeeklyUtilisation?: number;
  /** Id of the booking this one hard-clashes with — present for `time-clash`. */
  clashWithBookingId?: string;
}

export type PlacementResult =
  | { ok: true }
  | { ok: false; reason: PlacementRejectionReason; detail: PlacementRejectionDetail };

/**
 * Diagnostic variant of {@link canPlace}: instead of collapsing to a boolean it
 * returns WHY a candidate can (or cannot) take `booking`, capturing the exact
 * capacity numbers for the failing day/week. `canPlace` is a thin wrapper over
 * this so the placement algorithm and the diagnostics share one source of truth.
 */
function evaluatePlacement(state: CandidateState, booking: Booking): PlacementResult {
  // Hard time-clash against anything already on the candidate.
  //
  // A full-day booking is modelled as a whole-calendar-day (00:00–24:00)
  // PLACEHOLDER for the timeline, not a literal all-day lockout: its real load
  // is the resource's working hours for that day. So a full-day booking must
  // NOT hard-clash with smaller same-day work (e.g. a 1h meeting) — it can
  // coexist as long as the true worked hours still fit the day's capacity,
  // which the day-capacity check below enforces. Genuine wall-clock contention
  // is only meaningful between non-full-day bookings, so the clash gate is
  // limited to those; overloading a day with a full-day booking is caught by
  // the capacity check, not the clash check.
  const clash = state.occupied.find(
    (occ) =>
      !isFullDayBooking(booking) &&
      !isFullDayBooking(occ) &&
      bookingSegmentsOverlap(booking, occ),
  );
  if (clash) {
    return {
      ok: false,
      reason: 'time-clash',
      detail: { clashWithBookingId: String(clash.id) },
    };
  }
  // Capacity: every affected day must stay ≤ 100%. The incoming booking's hours
  // are its ORIGIN's real worked hours (a full-day move is charged the origin's
  // day, not the candidate's whole day), so a candidate with a little existing
  // work still has room for a sub-capacity full-day booking.
  for (const key of bookingDateKeys(booking)) {
    const cap = state.capacity.get(key) ?? dayCapacityFor(state.resource, key, DEFAULT_PROFILE);
    const used = state.usedHours.get(key) ?? 0;
    const add = state.incomingHoursOnDay(booking, key, cap);
    // `incomingHoursOnDay` clamps full/half-day hours DOWN to the candidate's
    // own day capacity, so a day the candidate does not work at all resolves to
    // 0 added hours and would otherwise "fit". Re-resolve the hours with an
    // uncapped capacity to get the booking's real (origin-sized) requirement:
    // if the booking genuinely needs work on this day but the candidate does
    // not work it, the placement must fail. A day neither side works (e.g. a
    // weekend inside a multi-day full-day booking) requires 0 hours and stays
    // acceptable.
    const required = state.incomingHoursOnDay(booking, key, Number.POSITIVE_INFINITY);
    if ((cap <= EPSILON && required > EPSILON) || used + add > cap + EPSILON) {
      return {
        ok: false,
        reason: 'day-capacity',
        detail: { dayKey: key, dayCapacity: cap, used, add },
      };
    }
  }
  // Weekly over-utilisation: the target must not exceed 100% for any affected
  // week after absorbing the booking.
  const projected = projectedWeeklyUtilisation(state, booking);
  if (projected > 100 + EPSILON) {
    return {
      ok: false,
      reason: 'week-capacity',
      detail: { projectedWeeklyUtilisation: projected },
    };
  }
  return { ok: true };
}

/**
 * Can this candidate take `booking` without a time clash, without breaching
 * 100% on any affected DAY, AND without pushing the candidate's WEEKLY
 * utilisation over 100% on any affected week? The weekly check ensures a
 * rebalance never lands work on a resource that would itself end up
 * over-utilised (e.g. one already loaded elsewhere in the same week), matching
 * the burnout model so the shown plan only targets genuinely under-utilised
 * resources.
 */
function canPlace(state: CandidateState, booking: Booking): boolean {
  return evaluatePlacement(state, booking).ok;
}

/** Global escape hatch to turn on distribution diagnostics from the console. */
interface SurplusDistributionDebugGlobal {
  __EB_SURPLUS_DISTRIBUTION_DEBUG__?: boolean;
}

/** Whether to collect + log per-candidate rejection diagnostics. */
function surplusDistributionDebugEnabled(explicit?: boolean): boolean {
  if (explicit !== undefined) return explicit;
  if (typeof globalThis === 'undefined') return false;
  return (globalThis as SurplusDistributionDebugGlobal).__EB_SURPLUS_DISTRIBUTION_DEBUG__ === true;
}

function place(state: CandidateState, booking: Booking): void {
  state.occupied.push(booking);
  bookingDateKeys(booking).forEach((key) => {
    const cap = state.capacity.get(key) ?? dayCapacityFor(state.resource, key, DEFAULT_PROFILE);
    state.usedHours.set(
      key,
      (state.usedHours.get(key) ?? 0) + state.incomingHoursOnDay(booking, key, cap),
    );
  });
}

/** Free-capacity % of a candidate across the affected days (0–100). */
function freeCapacityPct(state: CandidateState, affectedDateKeys: string[]): number {
  let cap = 0;
  let used = 0;
  affectedDateKeys.forEach((key) => {
    const c = state.capacity.get(key) ?? 0;
    cap += c;
    used += Math.min(state.usedHours.get(key) ?? 0, c);
  });
  if (cap <= 0) return 0;
  return Math.max(0, Math.min(100, ((cap - used) / cap) * 100));
}

// ── Public entry point ─────────────────────────────────────────────────────

export function distributeSurplusBookings(
  params: DistributeSurplusParams,
): SurplusDistributionResult {
  const {
    surplus,
    resources,
    existingBookings,
    globalProfile,
    atRiskResourceId,
    preferredResourceId,
    excludeResourceIds,
  } = params;
  const atRiskResource = params.atRiskResource ?? null;

  // Real worked hours a MOVING surplus booking adds on a day. Full/half-day
  // bookings are sized by their ORIGIN's working-hours pattern (the at-risk
  // resource being relieved), NOT the target candidate's day — a full-day
  // booking made by a 6.5h/day person stays 6.5h when measured against an
  // 8h/day candidate, so it no longer automatically consumes the whole target
  // day. Hourly hours are wall-clock (origin-independent). Falls back to the
  // candidate's own day capacity when the origin isn't known.
  //
  // Full/half-day hours are additionally clamped DOWN to the candidate's own
  // day capacity: an origin working a LONGER day (8h) must not be charged in
  // full against a shorter-day candidate (7.2h), which would falsely trip the
  // day/week capacity gate on a completely free candidate. Hourly bookings are
  // never clamped so genuinely oversized ones are still rejected.
  const incomingHoursOnDay: IncomingHoursResolver = (booking, dateKey, fallbackCapacity) => {
    const originCapacity = atRiskResource
      ? dayCapacityFor(atRiskResource, dateKey, globalProfile)
      : fallbackCapacity;
    const hours = bookingHoursOnDay(booking, dateKey, originCapacity);
    const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
    if (dt === 'hourly') return hours;
    return Math.min(hours, bookingHoursOnDay(booking, dateKey, fallbackCapacity));
  };

  const excludeSet = new Set((excludeResourceIds ?? []).map(String));
  // The excluded resources that actually exist in the candidate pool — surfaced
  // on the result so callers can explain a "no suitable resource" outcome.
  const excludedResourceIds = resources
    .filter((r) => excludeSet.has(String(r.id)))
    .map((r) => String(r.id));

  if (surplus.length === 0) {
    return {
      mode: 'none',
      assignments: [],
      targetResourceIds: [],
      blockedBookingIds: [],
      excludedResourceIds,
    };
  }

  // Assess capacity over ONLY the exact days each surplus booking occupies.
  // Bookings never change date — they only move to a resource that is
  // under-utilised on those same days — so `buildCandidateState` caches usage
  // and `freeCapacityPct` reflects headroom on the required dates alone.
  const affectedDateKeys = Array.from(
    new Set(surplus.flatMap((b) => bookingDateKeys(b))),
  );
  const surplusIds = new Set(surplus.map((b) => String(b.id)));

  // Existing bookings excluding the surplus themselves (they are being moved).
  const baseBookings = existingBookings.filter((b) => !surplusIds.has(String(b.id)));

  const debug = surplusDistributionDebugEnabled(params.debug);
  const surplusById = new Map(surplus.map((b) => [String(b.id), b]));

  // Explain, per blocked booking, WHY every real candidate (eligible AND
  // excluded) was rejected. Evaluated against the BASELINE load (existing
  // bookings only, not surplus already placed this run) so each reason reflects
  // the candidate's own standing capacity on the booking's exact days. This is
  // the diagnostic that turns a boolean "no suitable resource" into a concrete,
  // per-resource reason (time-clash / day-capacity / week-capacity).
  const collectBlockedDiagnostics = (
    blockedIds: string[],
  ): BlockedBookingDiagnostic[] | undefined => {
    if (!debug || blockedIds.length === 0) return undefined;
    const candidatePool = resources.filter(
      (r) => r.id !== 'unallocated' && String(r.id) !== String(atRiskResourceId),
    );
    return blockedIds.map((bid) => {
      const booking = surplusById.get(String(bid));
      const dayKeys = booking ? bookingDateKeys(booking) : [];
      const rejections: CandidateRejectionDiagnostic[] = booking
        ? candidatePool.map((r) => {
            const state = buildCandidateState(r, dayKeys, baseBookings, globalProfile, incomingHoursOnDay);
            const result = evaluatePlacement(state, booking);
            return {
              candidateId: String(r.id),
              candidateName: r.name ?? String(r.id),
              excluded: excludeSet.has(String(r.id)),
              result,
            };
          })
        : [];
      return {
        bookingId: String(bid),
        bookingDateKeys: dayKeys,
        durationType: booking
          ? normalizeDurationType(booking.durationType, booking.halfDayPeriod)
          : 'hourly',
        rejections,
      };
    });
  };

  const logBlockedDiagnostics = (diagnostics?: BlockedBookingDiagnostic[]): void => {
    if (!debug || !diagnostics || diagnostics.length === 0) return;
    diagnostics.forEach((d) => {
      console.debug(
        `[distributeSurplusBookings] blocked booking ${d.bookingId} `
          + `(${d.durationType}, days=${d.bookingDateKeys.join(',')}) — candidate rejections:`,
        d.rejections.map((r) => ({
          candidate: `${r.candidateName} (${r.candidateId})`,
          excluded: r.excluded,
          reason: r.result.ok ? 'placeable-at-baseline' : r.result.reason,
          ...(r.result.ok ? {} : r.result.detail),
        })),
      );
    });
  };

  // Eligible pool: real resources, excluding only the at-risk resource itself
  // and any caller-supplied exclusions.
  //
  // Note: we deliberately do NOT drop a resource just because it has a hard
  // time-clash against SOME surplus booking. `canPlace` already rejects each
  // individual booking on a time-clash (and on breaching 100% for a day), so a
  // resource that clashes with only one of several surplus bookings can still
  // absorb the ones it does not clash with. Excluding the whole resource here
  // would remove that partial capacity and could block bookings that actually
  // fit, producing the inconsistency where a candidate is recommended but the
  // redistribution plan finds "no suitable resource" for everything.
  const eligible = resources.filter((r) => {
    if (r.id === 'unallocated') return false;
    if (String(r.id) === String(atRiskResourceId)) return false;
    if (excludeSet.has(String(r.id))) return false;
    return true;
  });

  if (eligible.length === 0) {
    const blockedBookingIds = surplus.map((b) => String(b.id));
    const blockedDiagnostics = collectBlockedDiagnostics(blockedBookingIds);
    logBlockedDiagnostics(blockedDiagnostics);
    return {
      mode: 'none',
      assignments: [],
      targetResourceIds: [],
      blockedBookingIds,
      excludedResourceIds,
      blockedDiagnostics,
    };
  }

  const nameOf = (r: Resource) => r.name ?? String(r.id);

  // Score each eligible candidate on its baseline free capacity.
  const scored = eligible
    .map((r) => {
      const state = buildCandidateState(r, affectedDateKeys, baseBookings, globalProfile, incomingHoursOnDay);
      const free = freeCapacityPct(state, affectedDateKeys);
      return { resource: r, score: scoreCandidate(r, atRiskResource, free), free };
    })
    .sort((a, b) => {
      if (Math.abs(b.score - a.score) > EPSILON) return b.score - a.score;
      if (Math.abs(b.free - a.free) > EPSILON) return b.free - a.free;
      return String(a.resource.id).localeCompare(String(b.resource.id));
    });

  // ── Attempt 1: a single resource that can absorb ALL surplus ──────────────
  const trySingle = (resource: Resource): BookingAssignment[] | null => {
    const state = buildCandidateState(resource, affectedDateKeys, baseBookings, globalProfile, incomingHoursOnDay);
    const assignments: BookingAssignment[] = [];
    for (const b of surplus) {
      if (!canPlace(state, b)) return null;
      place(state, b);
      assignments.push({
        bookingId: String(b.id),
        candidateResourceId: String(resource.id),
        candidateResourceName: nameOf(resource),
      });
    }
    return assignments;
  };

  // Honour an explicit preferred resource first, then the best-scoring pool.
  const singleOrder: Resource[] = [];
  if (preferredResourceId) {
    const preferred = eligible.find((r) => String(r.id) === String(preferredResourceId));
    if (preferred) singleOrder.push(preferred);
  }
  scored.forEach((s) => {
    if (!singleOrder.some((r) => String(r.id) === String(s.resource.id))) {
      singleOrder.push(s.resource);
    }
  });

  for (const resource of singleOrder) {
    const assignments = trySingle(resource);
    if (assignments) {
      return {
        mode: 'single',
        assignments,
        targetResourceIds: [String(resource.id)],
        blockedBookingIds: [],
        excludedResourceIds,
      };
    }
  }

  // ── Attempt 2: spread greedily across multiple resources ──────────────────
  const states = new Map<string, CandidateState>();
  const stateFor = (r: Resource): CandidateState => {
    const key = String(r.id);
    let st = states.get(key);
    if (!st) {
      st = buildCandidateState(r, affectedDateKeys, baseBookings, globalProfile, incomingHoursOnDay);
      states.set(key, st);
    }
    return st;
  };

  // Place the hardest-to-fit bookings first (full days before hourly).
  const placementOrder = [...surplus].sort((a, b) => {
    const weight = (bk: Booking) => {
      const dt = normalizeDurationType(bk.durationType, bk.halfDayPeriod);
      if (dt === 'full-day') return 0;
      if (dt.startsWith('half-day')) return 1;
      return 2;
    };
    return weight(a) - weight(b);
  });

  const assignments: BookingAssignment[] = [];
  const blockedBookingIds: string[] = [];

  // Find the best candidate that can take `bk` right now (highest score, then
  // most free capacity). Returns null when nobody has room.
  const bestCandidateFor = (bk: Booking): Resource | null => {
    let chosen: { resource: Resource; free: number; score: number } | null = null;
    for (const s of scored) {
      const state = stateFor(s.resource);
      if (!canPlace(state, bk)) continue;
      const free = freeCapacityPct(state, affectedDateKeys);
      if (
        !chosen ||
        s.score - chosen.score > EPSILON ||
        (Math.abs(s.score - chosen.score) <= EPSILON && free > chosen.free + EPSILON)
      ) {
        chosen = { resource: s.resource, free, score: s.score };
      }
    }
    return chosen?.resource ?? null;
  };

  for (const booking of placementOrder) {
    // 1. Try to place the WHOLE booking on a single resource.
    const whole = bestCandidateFor(booking);
    if (whole) {
      place(stateFor(whole), booking);
      assignments.push({
        bookingId: String(booking.id),
        candidateResourceId: String(whole.id),
        candidateResourceName: nameOf(whole),
      });
      continue;
    }

    // 2. No single resource is free across the whole booking. If it spans more
    //    than one day, SPLIT it per day and place each day on a resource with
    //    room that day. Slices are on distinct days, so a candidate chosen for
    //    one day does not affect another day's placement — we can evaluate each
    //    against the current state and only commit when EVERY day found a home.
    const daySlices = splitBookingByDay(booking);
    if (daySlices.length > 1) {
      const perDay: Array<{ slice: Booking; daySlice: BookingDaySlice; resource: Resource }> = [];
      let allPlaced = true;
      for (const { slice, daySlice } of daySlices) {
        const target = bestCandidateFor(slice);
        if (!target) {
          allPlaced = false;
          break;
        }
        perDay.push({ slice, daySlice, resource: target });
      }
      if (allPlaced) {
        for (const { slice, daySlice, resource } of perDay) {
          place(stateFor(resource), slice);
          assignments.push({
            bookingId: String(booking.id),
            candidateResourceId: String(resource.id),
            candidateResourceName: nameOf(resource),
            daySlice,
          });
        }
        continue;
      }
    }

    // 3. Neither whole nor a clean per-day split fits on the booking's OWN
    //    dates on any safe resource. Bookings never change date, so it stays
    //    blocked and surfaces as "No suitable resource found".
    blockedBookingIds.push(String(booking.id));
  }

  const blockedDiagnostics = collectBlockedDiagnostics(blockedBookingIds);
  logBlockedDiagnostics(blockedDiagnostics);

  if (assignments.length === 0) {
    return {
      mode: 'none',
      assignments: [],
      targetResourceIds: [],
      blockedBookingIds,
      excludedResourceIds,
      blockedDiagnostics,
    };
  }

  const targetResourceIds = Array.from(
    new Set(assignments.map((a) => a.candidateResourceId)),
  );

  return {
    mode: targetResourceIds.length === 1 ? 'single' : 'spread',
    assignments,
    targetResourceIds,
    blockedBookingIds,
    excludedResourceIds,
    blockedDiagnostics,
  };
}

/**
 * The concrete booking mutations needed to commit a distribution plan.
 *
 *   • `updates` — a whole booking simply moves to another resource.
 *   • `creates` + `deletes` — a booking that was SPLIT per day is replaced by
 *     one new per-day booking on each day's target resource, and the original
 *     multi-day booking is deleted. A split is only ever produced when EVERY
 *     day of the booking found a home, so the per-day creates fully account for
 *     the original's work (no day is silently dropped).
 */
export interface ReassignmentMutations {
  updates: Array<{ bookingId: string; resourceId: string }>;
  creates: Booking[];
  deletes: string[];
}

/**
 * Translate distribution assignments into concrete booking mutations, honouring
 * per-day splits. `newId` mints an id for each per-day booking created from a
 * split. Callers apply the result via their own create/update/delete APIs, so
 * every commit path (report + calendar) produces the SAME per-day bookings.
 */
export function buildReassignmentMutations(
  assignments: BookingAssignment[],
  bookings: Booking[],
  newId: () => string,
): ReassignmentMutations {
  const byBooking = new Map<string, BookingAssignment[]>();
  assignments.forEach((a) => {
    const list = byBooking.get(a.bookingId) ?? [];
    list.push(a);
    byBooking.set(a.bookingId, list);
  });

  const updates: Array<{ bookingId: string; resourceId: string }> = [];
  const creates: Booking[] = [];
  const deletes: string[] = [];

  byBooking.forEach((list, bookingId) => {
    const sliced = list.filter((a) => a.daySlice);
    if (sliced.length === 0) {
      // Whole-booking move (there is exactly one assignment for it).
      updates.push({ bookingId, resourceId: String(list[0].candidateResourceId) });
      return;
    }
    const original = bookings.find((b) => String(b.id) === String(bookingId));
    if (!original) return;
    for (const a of sliced) {
      const slice = a.daySlice!;
      creates.push({
        ...original,
        id: newId(),
        resourceId: String(a.candidateResourceId),
        resourceIds: [String(a.candidateResourceId)],
        startTime: slice.startTime,
        endTime: slice.endTime,
        durationType: slice.durationType,
        halfDayPeriod: slice.halfDayPeriod,
      });
    }
    deletes.push(String(bookingId));
  });

  return { updates, creates, deletes };
}

/**
 * A field-level update to an existing booking, produced by the non-reassign
 * conflict resolutions (reduce hours / delay start). Callers apply it through
 * their own `updateBooking(id, changes)` API so every commit path (report +
 * calendar) mutates bookings identically.
 */
export interface BookingFieldUpdate {
  bookingId: string;
  changes: Partial<Booking>;
}

/** Keep the original string's UTC-vs-local shape when rewriting a timestamp. */
function formatLike(original: string, date: Date): string {
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(original)) return date.toISOString();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`
  );
}

function shiftDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return formatLike(iso, d);
}

function calendarDayDiff(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

/** Concrete changes for a set of "reduce hours" resolutions. */
export interface ReductionMutations {
  updates: BookingFieldUpdate[];
  /** Per-day bookings a multi-day booking is split into, after its first day. */
  creates: Booking[];
  /** Before/after hours of each reduced booking, per day. */
  details: WhatIfBookingReduction[];
}

const QUARTER_HOUR_MIN = 15;

/**
 * Translate "reduce hours" resolutions into concrete booking changes.
 *
 * Each day of the booking keeps the same share of its hours: a 3-day full-day
 * booking cut from 24h to 18h becomes 6h on every day (rounded to 15 minutes,
 * with the rounding spread so the total still matches the chosen hours). Every
 * day becomes an hourly window starting where the day did (09:00/13:00 for a
 * day-shaped booking). A single-day booking is updated in place; a multi-day
 * booking keeps its first day on the original booking and gets one new
 * booking per remaining day, because one booking can't hold a shorter window
 * on each of several days.
 *
 * Reductions that would not actually lower the booking's hours are dropped.
 */
// The preview builds these ids on every render, so it must not throw where
// crypto.randomUUID is unavailable (non-secure origins).
function newReductionBookingId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildReductionMutations(
  reductions: Array<{ bookingId: string; newWeeklyHours: number }>,
  bookings: Booking[],
  newId: () => string = newReductionBookingId,
  dayCapacityHours = 8,
): ReductionMutations {
  const result: ReductionMutations = { updates: [], creates: [], details: [] };

  reductions.forEach(({ bookingId, newWeeklyHours }) => {
    const booking = bookings.find((b) => String(b.id) === String(bookingId));
    if (!booking || !(newWeeklyHours > 0)) return;

    const segments = getBookingSegments(booking);
    if (segments.length === 0) return;

    const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
    const dayKeys = Array.from(new Set(segments.map((s) => s.dateKey))).sort();
    const before = dayKeys.map((key) => {
      const hours = bookingHoursOnDay(booking, key, dayCapacityHours);
      const daySegs = segments.filter((s) => s.dateKey === key);
      const segStart = Math.min(...daySegs.map((s) => s.startMin));
      const startMin = dt === 'hourly' ? segStart : segStart >= 720 ? 780 : 540;
      const endMin = dt === 'hourly'
        ? Math.max(...daySegs.map((s) => s.endMin))
        : Math.min(startMin + Math.round(hours * 60), 24 * 60);
      return { dateKey: key, startMin, endMin, hours };
    });
    const currentHours = before.reduce((sum, d) => sum + d.hours, 0);
    if (newWeeklyHours >= currentHours - EPSILON) return;

    // Split the target in 15-minute steps: each day gets its share rounded
    // down, then the leftover steps go to the days with the largest remainder.
    const ratio = newWeeklyHours / currentHours;
    const targetSteps = Math.max(before.length, Math.round((newWeeklyHours * 60) / QUARTER_HOUR_MIN));
    const exact = before.map((d) => ((d.hours * 60) / QUARTER_HOUR_MIN) * ratio);
    const maxSteps = before.map((d) => Math.floor((d.hours * 60) / QUARTER_HOUR_MIN + EPSILON));
    const steps = exact.map((e, i) => Math.min(maxSteps[i], Math.max(1, Math.floor(e + EPSILON))));
    let left = targetSteps - steps.reduce((a, b) => a + b, 0);
    const order = exact
      .map((e, i) => ({ i, rem: e - Math.floor(e + EPSILON) }))
      .sort((a, b) => b.rem - a.rem);
    for (let pass = 0; left > 0 && pass < 2; pass++) {
      for (const { i } of order) {
        if (left <= 0) break;
        if (steps[i] < maxSteps[i]) { steps[i]++; left--; }
      }
    }

    const days: WhatIfReductionDay[] = before.map((d, i) => {
      const afterEndMin = Math.min(d.startMin + steps[i] * QUARTER_HOUR_MIN, 24 * 60 - 1);
      return {
        dateKey: d.dateKey,
        beforeStartMin: d.startMin,
        beforeEndMin: d.endMin,
        beforeHours: d.hours,
        afterStartMin: d.startMin,
        afterEndMin,
        afterHours: (afterEndMin - d.startMin) / 60,
      };
    });
    const afterHours = days.reduce((sum, d) => sum + d.afterHours, 0);
    if (afterHours >= currentHours - EPSILON) return;

    const dayFields = (d: WhatIfReductionDay): Partial<Booking> => ({
      startTime: isoForDayMinute(d.dateKey, d.afterStartMin),
      endTime: isoForDayMinute(d.dateKey, d.afterEndMin),
      durationType: 'hourly',
      halfDayPeriod: undefined,
      durationHours: d.afterHours,
    });

    result.updates.push({ bookingId: String(booking.id), changes: dayFields(days[0]) });
    days.slice(1).forEach((d) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { auditHistory, isRecurrenceParent, ...rest } = booking;
      result.creates.push({ ...rest, ...dayFields(d), id: newId() } as Booking);
    });
    result.details.push({
      bookingId: String(booking.id),
      targetHours: newWeeklyHours,
      beforeHours: currentHours,
      afterHours,
      days,
    });
  });

  return result;
}

/**
 * Translate "delay start" resolutions into concrete booking updates: the whole
 * booking slides by the whole-day difference between its current start and the
 * chosen slot, preserving its duration and time-of-day.
 */
export function buildDelayMutations(
  delays: Array<{ bookingId: string; newStartDate: string }>,
  bookings: Booking[],
): BookingFieldUpdate[] {
  const updates: BookingFieldUpdate[] = [];

  delays.forEach(({ bookingId, newStartDate }) => {
    const booking = bookings.find((b) => String(b.id) === String(bookingId));
    if (!booking) return;
    const target = new Date(newStartDate);
    const current = new Date(booking.startTime);
    if (Number.isNaN(target.getTime()) || Number.isNaN(current.getTime())) return;

    const deltaDays = calendarDayDiff(current, target);
    if (deltaDays === 0) return;

    updates.push({
      bookingId: String(booking.id),
      changes: {
        startTime: shiftDays(booking.startTime, deltaDays),
        endTime: shiftDays(booking.endTime, deltaDays),
      },
    });
  });

  return updates;
}

const isConfirmedBooking = (status?: string): boolean =>
  status === 'confirmed' || status === 'completed';

const bookingStartMs = (startTime: string): number => {
  const t = Date.parse(startTime);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * "Keep priority" ordering: the most-committed work stays put and only the
 * marginal bookings are offered for reassignment — confirmed/completed first,
 * then earliest start, then lowest id.
 */
function byKeepPriority(a: Booking, b: Booking): number {
  const aConf = isConfirmedBooking(a.status);
  const bConf = isConfirmedBooking(b.status);
  if (aConf !== bConf) return aConf ? -1 : 1;
  const aStart = bookingStartMs(a.startTime);
  const bStart = bookingStartMs(b.startTime);
  if (aStart !== bStart) return aStart - bStart;
  return String(a.id).localeCompare(String(b.id));
}

/**
 * Select the minimal set of over-capacity bookings that must move so a
 * resource's day-by-day load no longer exceeds its working-hours capacity.
 *
 * Unlike {@link partitionConflictBookings} — which keeps a single anchor and
 * sweeps every other booking into the surplus — this keeps AS MANY bookings as
 * fit under 100% per day and moves only the ones that overflow. Bookings are
 * considered in "keep priority" order (confirmed first, then earliest start,
 * then lowest id) so the most-committed work stays put and only the marginal,
 * over-capacity bookings are offered for reassignment.
 *
 * A resource that is genuinely over-utilised therefore surrenders exactly the
 * bookings causing the breach — never every booking, and never unrelated
 * bookings that already fit within capacity.
 */
export function selectOverCapacitySurplus(
  bookings: Booking[],
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): { kept: Booking[]; surplus: Booking[] } {
  const ordered = [...bookings].sort(byKeepPriority);

  const usedByDay = new Map<string, number>();
  const kept: Booking[] = [];
  const surplus: Booking[] = [];

  for (const booking of ordered) {
    const dayKeys = bookingDateKeys(booking);
    const fits = dayKeys.every((key) => {
      const cap = dayCapacityFor(resource, key, globalProfile);
      const used = usedByDay.get(key) ?? 0;
      const add = bookingHoursOnDay(booking, key, cap);
      return used + add <= cap + EPSILON;
    });

    if (fits) {
      kept.push(booking);
      dayKeys.forEach((key) => {
        const cap = dayCapacityFor(resource, key, globalProfile);
        usedByDay.set(key, (usedByDay.get(key) ?? 0) + bookingHoursOnDay(booking, key, cap));
      });
    } else {
      surplus.push(booking);
    }
  }

  return { kept, surplus };
}

/**
 * The Mon–Sun day keys of every distinct ISO week a set of bookings touches,
 * keyed by the week's Monday.
 */
function affectedIsoWeeks(bookings: Booking[]): string[][] {
  const byStartKey = new Map<string, string[]>();
  bookings.forEach((b) => {
    bookingDateKeys(b).forEach((dk) => {
      const days = isoWeekDaysFor(dk);
      byStartKey.set(days[0], days);
    });
  });
  return Array.from(byStartKey.values());
}

/**
 * Weekly utilisation (%) of a resource over one ISO week, using the same
 * working-hours accounting as the heatmap and {@link projectedWeeklyUtilisation}
 * (booked hours ÷ available hours). Deliberately measured here rather than via
 * `computeWeekUtilisation` so a single-day `full-day` booking is counted as a
 * whole day regardless of how its `endTime` is encoded.
 */
function weekUtilisationPct(
  resource: Resource,
  weekDays: string[],
  bookings: Booking[],
  globalProfile: WorkingHoursProfile,
): number {
  let available = 0;
  let booked = 0;
  weekDays.forEach((day) => {
    const cap = dayCapacityFor(resource, day, globalProfile);
    available += cap;
    bookings.forEach((b) => {
      if (bookingDateKeys(b).includes(day)) booked += bookingHoursOnDay(b, day, cap);
    });
  });
  if (available <= 0) return booked > 0 ? Infinity : 0;
  return (booked / available) * 100;
}

/**
 * Select the MINIMAL set of bookings that must move so a resource is no longer
 * over-utilised — measured against BOTH models that define "at risk":
 *   • per-DAY working-hours capacity (no day over 100%), and
 *   • per-ISO-WEEK utilisation (booked ÷ available hours over Mon–Sun), the
 *     same weekly measure the burnout engine uses. The week must end up
 *     BELOW {@link WEEK_UTILISATION_LIMIT_PCT}, because `computeBurnoutRisk`
 *     already flags a week AT 100% as high risk — stopping at exactly 100%
 *     would leave the resource still at risk with nothing to move.
 *
 * Bookings are considered in {@link byKeepPriority} order (confirmed first, then
 * earliest start, then lowest id) and each one is KEPT while the resource stays
 * ≤100% for every affected day and every affected week; only bookings whose
 * retention would leave it over 100% are pushed to the surplus. Once the
 * remaining load is under 100% every subsequent booking simply fits and stays,
 * so a booking is never moved unless it is needed to clear the breach.
 *
 * This handles the per-day-breach case AND the weekly-trend case (a week in the
 * 80–110% band where no single day exceeds capacity), replacing the
 * "keep one anchor, move everything else" fallback for burnout suggestions.
 */
export function selectMinimalSurplusToUnder100(
  bookings: Booking[],
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): { kept: Booking[]; surplus: Booking[] } {
  const ordered = [...bookings].sort(byKeepPriority);
  const weeks = affectedIsoWeeks(bookings);

  const usedByDay = new Map<string, number>();
  const kept: Booking[] = [];
  const surplus: Booking[] = [];

  const weeklyWithinLimit = (candidate: Booking): boolean => {
    const probe = [...kept, candidate];
    return weeks.every(
      (weekDays) =>
        weekUtilisationPct(resource, weekDays, probe, globalProfile) <
        WEEK_UTILISATION_LIMIT_PCT - EPSILON,
    );
  };

  for (const booking of ordered) {
    const dayKeys = bookingDateKeys(booking);
    const dailyFits = dayKeys.every((key) => {
      const cap = dayCapacityFor(resource, key, globalProfile);
      const used = usedByDay.get(key) ?? 0;
      return used + bookingHoursOnDay(booking, key, cap) <= cap + EPSILON;
    });

    if (dailyFits && weeklyWithinLimit(booking)) {
      kept.push(booking);
      dayKeys.forEach((key) => {
        const cap = dayCapacityFor(resource, key, globalProfile);
        usedByDay.set(key, (usedByDay.get(key) ?? 0) + bookingHoursOnDay(booking, key, cap));
      });
    } else {
      surplus.push(booking);
    }
  }

  return { kept, surplus };
}

/**
 * Derive the exact surplus bookings a rebalance suggestion should move.
 *
 * Mirrors the logic in `BurnoutRiskPanel.onApply` so the overlay preview and
 * the calendar apply path resolve the SAME set of bookings:
 *   • When the suggestion carries explicit `atRiskBookingIds` (conflict
 *     resolution), those exact bookings are the surplus.
 *   • Otherwise (burnout suggestions) the surplus is the MINIMAL set needed to
 *     bring the resource to/under 100%, via
 *     {@link selectMinimalSurplusToUnder100} — it covers both the per-day
 *     breach case and the weekly-trend case (a week in the 80–110% band where
 *     no single day exceeds capacity), so everything that already fits stays
 *     on the original resource.
 *   • A genuine same-day overlap still surrenders one booking per overlap
 *     cluster (the literal clash must go), and only then are further bookings
 *     added if the remaining load is still over 100%.
 *   • The anchor-keeping partition ({@link partitionConflictBookings}, which
 *     keeps ONE booking and moves the rest) is a LAST RESORT, used only when
 *     the resource / working profile aren't available to measure capacity.
 */
export function deriveSurplusBookings(
  suggestion: RebalancingSuggestion,
  bookings: Booking[],
  atRiskResource?: Resource | null,
  globalProfile?: WorkingHoursProfile,
): Booking[] {
  const explicitIds = suggestion.atRiskBookingIds ?? [];
  if (explicitIds.length > 0) {
    const idSet = new Set(explicitIds.map(String));
    return bookings.filter((b) => idSet.has(String(b.id)));
  }

  const targetWeekStart =
    suggestion.affectedWeekStarts.length > 0
      ? suggestion.affectedWeekStarts[0]
      : null;
  if (!targetWeekStart) return [];

  const filterFrom = new Date(targetWeekStart);
  filterFrom.setDate(filterFrom.getDate() - 1);
  const targetWeekEnd = new Date(targetWeekStart);
  targetWeekEnd.setDate(targetWeekEnd.getDate() + 7);

  const weekBookings = bookings.filter(
    (b) =>
      String(b.resourceId) === String(suggestion.atRiskResourceId) &&
      b.status !== 'cancelled' &&
      new Date(b.startTime) < targetWeekEnd &&
      new Date(b.endTime) > filterFrom,
  );

  if (atRiskResource && globalProfile) {
    // Genuine same-day double-bookings must lose one booking per overlap
    // cluster to remove the literal clash; the anchors that remain are then
    // measured against the daily + weekly models and only trimmed further if
    // they are still over 100%.
    const { anchors, surplus: overlapSurplus } = selectOverlapSurplus(weekBookings);
    if (overlapSurplus.length > 0) {
      const extra = selectMinimalSurplusToUnder100(anchors, atRiskResource, globalProfile).surplus;
      return [...overlapSurplus, ...extra];
    }

    // Pure over-utilisation (per-day breach and/or weekly trend): move only the
    // bookings needed to get back to/under 100%.
    return selectMinimalSurplusToUnder100(weekBookings, atRiskResource, globalProfile).surplus;
  }

  const surplus = selectOverlapSurplus(weekBookings).surplus;
  if (surplus.length > 0) return surplus;

  const projectBookings = weekBookings.filter(
    (b) => b.projectName === suggestion.projectName || b.projectId === suggestion.projectName,
  );
  const projectSurplus = partitionConflictBookings(projectBookings).surplus;
  if (projectSurplus.length > 0) return projectSurplus;

  // Last resort: the top project had nothing to spare (e.g. a single booking),
  // but the resource is still at risk. Keep one anchor across ALL its bookings
  // this week and surface the rest, so the plan always shows the candidate
  // bookings to move instead of an empty "no suitable resource" state.
  return partitionConflictBookings(weekBookings).surplus;
}

/** Summary label for the destination of a distribution: a single name or "N resources". */
export function summariseTargetResources(
  result: SurplusDistributionResult,
  resources: Resource[],
): string {
  const ids = result.targetResourceIds;
  if (ids.length === 0) return '—';
  if (ids.length === 1) {
    const r = resources.find((x) => String(x.id) === String(ids[0]));
    return r?.name ?? ids[0];
  }
  return `${ids.length} resources`;
}
